import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * useTableView — shared state for the Promang Table View.
 *
 * Manages column visibility/order/width, grouping, density and user-defined
 * "saved views". Column layout, grouping and the active saved view are mirrored
 * into the URL (so links are shareable) and persisted to localStorage so the
 * choice survives reloads. Density uses a single global preference key.
 *
 * The page owns the filter/sort "controller". It passes `getSnapshot` (read the
 * current filter/sort state) and `applySnapshot` (restore it) so saved views can
 * capture and re-apply filters too.
 */

const DENSITY_KEY = "promang_table_density";

const readJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / privacy mode */
  }
};

const setUrlParam = (key, value) => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (value == null || value === "" || value === "none") params.delete(key);
    else params.set(key, value);
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}`
    );
  } catch {
    /* ignore */
  }
};
const getUrlParam = (key) => {
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch {
    return null;
  }
};

export function useTableView({
  tableKey,
  columns = [],
  getSnapshot,
  applySnapshot,
  defaultGroup = "none",
  ensureVisibleKeys = [],
}) {
  const COLS_LS = `pm_tv.${tableKey}.cols`;
  const GROUP_LS = `pm_tv.${tableKey}.group`;
  const VIEWS_LS = `pm_tv.${tableKey}.views`;

  const allKeys = useMemo(() => columns.map((c) => c.key), [columns]);
  const lockedKeys = useMemo(
    () => columns.filter((c) => c.always).map((c) => c.key),
    [columns]
  );
  const defaultVisible = useMemo(
    () => columns.filter((c) => c.defaultVisible !== false).map((c) => c.key),
    [columns]
  );

  // ── Column layout (order / visible / widths) ──────────────────────────────
  const [colState, setColState] = useState(() => {
    const urlCols = getUrlParam("cols");
    const stored = readJSON(COLS_LS);
    let visible = defaultVisible;
    if (urlCols) {
      const fromUrl = urlCols.split(",").filter((k) => allKeys.includes(k));
      if (fromUrl.length) visible = [...new Set([...lockedKeys, ...fromUrl])];
    } else if (stored?.visible?.length) {
      visible = stored.visible.filter((k) => allKeys.includes(k));
      const storedOrder = stored.order || [];
      allKeys
        .filter((k) => !storedOrder.includes(k) && defaultVisible.includes(k))
        .forEach((k) => {
          if (!visible.includes(k)) visible.push(k);
        });
    }
    const ENSURE_LS = `pm_tv.${tableKey}.ensured`;
    const alreadyEnsured = readJSON(ENSURE_LS) || [];
    const pendingEnsure = (ensureVisibleKeys || []).filter(
      (k) => allKeys.includes(k) && defaultVisible.includes(k) && !alreadyEnsured.includes(k)
    );
    if (pendingEnsure.length) {
      pendingEnsure.forEach((k) => {
        if (!visible.includes(k)) visible.push(k);
      });
      writeJSON(ENSURE_LS, [...alreadyEnsured, ...pendingEnsure]);
    }
    const order = stored?.order?.length
      ? [...stored.order.filter((k) => allKeys.includes(k)), ...allKeys.filter((k) => !stored.order.includes(k))]
      : allKeys;
    return { order, visible, widths: stored?.widths || {} };
  });

  const visibleKeys = useMemo(
    () => colState.order.filter((k) => colState.visible.includes(k)),
    [colState]
  );

  useEffect(() => {
    writeJSON(COLS_LS, colState);
    // Only store non-default visible sets in the URL to keep links clean
    const isDefault =
      colState.visible.length === defaultVisible.length &&
      defaultVisible.every((k) => colState.visible.includes(k));
    setUrlParam("cols", isDefault ? null : visibleKeys.join(","));
  }, [colState]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleColumn = useCallback(
    (key) => {
      if (lockedKeys.includes(key)) return;
      setColState((cs) => ({
        ...cs,
        visible: cs.visible.includes(key)
          ? cs.visible.filter((k) => k !== key)
          : [...cs.visible, key],
      }));
    },
    [lockedKeys]
  );

  const reorderColumn = useCallback((from, to) => {
    setColState((cs) => {
      const order = [...cs.order];
      const [moved] = order.splice(from, 1);
      order.splice(to, 0, moved);
      return { ...cs, order };
    });
  }, []);

  const resizeColumn = useCallback((key, width) => {
    setColState((cs) => ({ ...cs, widths: { ...cs.widths, [key]: width } }));
  }, []);

  const resetColumns = useCallback(() => {
    setColState({ order: allKeys, visible: defaultVisible, widths: {} });
  }, [allKeys, defaultVisible]);

  // ── Grouping ──────────────────────────────────────────────────────────────
  const [groupBy, setGroupByState] = useState(
    () => getUrlParam("group") || readJSON(GROUP_LS) || defaultGroup
  );
  const setGroupBy = useCallback(
    (value) => {
      setGroupByState(value || "none");
      writeJSON(GROUP_LS, value || "none");
      setUrlParam("group", value === "none" ? null : value);
    },
    [GROUP_LS]
  );

  // ── Density (global preference) ───────────────────────────────────────────
  const [density, setDensityState] = useState(() => {
    try {
      return localStorage.getItem(DENSITY_KEY) || "comfortable";
    } catch {
      return "comfortable";
    }
  });
  const setDensity = useCallback((value) => {
    setDensityState(value);
    try {
      localStorage.setItem(DENSITY_KEY, value);
    } catch {
      /* ignore */
    }
  }, []);

  // ── Saved views ─────────────────────────────────────────────────────────
  const [views, setViews] = useState(() => readJSON(VIEWS_LS) || []);
  const [activeViewId, setActiveViewId] = useState(() => getUrlParam("tview"));

  useEffect(() => {
    writeJSON(VIEWS_LS, views);
  }, [views]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildCurrentState = useCallback(
    () => ({
      snapshot: getSnapshot ? getSnapshot() : {},
      group: groupBy,
      visible: colState.visible,
      order: colState.order,
      density,
    }),
    [getSnapshot, groupBy, colState, density]
  );

  const activeView = views.find((v) => v.id === activeViewId) || null;

  const dirty = useMemo(() => {
    if (!activeView) return false;
    const cur = buildCurrentState();
    return (
      JSON.stringify({ s: cur.snapshot, g: cur.group, v: cur.visible, o: cur.order }) !==
      JSON.stringify({
        s: activeView.state.snapshot,
        g: activeView.state.group,
        v: activeView.state.visible,
        o: activeView.state.order,
      })
    );
  }, [activeView, buildCurrentState]);

  const applyView = useCallback(
    (id) => {
      const v = views.find((x) => x.id === id);
      if (!v) return;
      setActiveViewId(id);
      setUrlParam("tview", id);
      if (v.state.order || v.state.visible) {
        setColState((cs) => ({
          order: v.state.order || cs.order,
          visible: v.state.visible || cs.visible,
          widths: cs.widths,
        }));
      }
      if (v.state.group != null) setGroupBy(v.state.group);
      if (v.state.density) setDensity(v.state.density);
      if (applySnapshot && v.state.snapshot) applySnapshot(v.state.snapshot);
    },
    [views, applySnapshot, setGroupBy, setDensity]
  );

  const saveAsView = useCallback(
    (name) => {
      const label = (name || "").trim();
      if (!label) return null;
      const id = `v${Date.now()}`;
      const view = { id, name: label, state: buildCurrentState() };
      setViews((prev) => [...prev, view]);
      setActiveViewId(id);
      setUrlParam("tview", id);
      return view;
    },
    [buildCurrentState]
  );

  const updateView = useCallback(() => {
    if (!activeViewId) return;
    setViews((prev) =>
      prev.map((v) =>
        v.id === activeViewId ? { ...v, state: buildCurrentState() } : v
      )
    );
  }, [activeViewId, buildCurrentState]);

  const deleteView = useCallback(
    (id) => {
      setViews((prev) => prev.filter((v) => v.id !== id));
      if (activeViewId === id) {
        setActiveViewId(null);
        setUrlParam("tview", null);
      }
    },
    [activeViewId]
  );

  // Keep ref of latest so consumers can read without re-subscribing
  const ref = useRef();
  ref.current = { groupBy, density, colState };

  return {
    // columns
    order: colState.order,
    visibleKeys,
    widths: colState.widths,
    toggleColumn,
    reorderColumn,
    resizeColumn,
    resetColumns,
    // grouping
    groupBy,
    setGroupBy,
    // density
    density,
    setDensity,
    // saved views
    views,
    activeViewId,
    activeView,
    dirty,
    applyView,
    saveAsView,
    updateView,
    deleteView,
  };
}

export default useTableView;
